import { DimensionType, getDimensionsFromItemsMap, getSeriesId, isDimension, } from '@lightdash/common';
import { getPivotedDataFromPivotDetails } from '../pivot/plottedData';
const getPivotGroupKey = (pivotValues) => pivotValues.map((pv) => `${pv.field}:${pv.value}`).join('|');
const limitToFirstNPivotGroups = (rowKeyValues, limit) => {
    const seenGroups = new Set();
    return rowKeyValues.filter((rowKey) => {
        if (typeof rowKey === 'string')
            return true;
        const groupKey = rowKey.pivotValues
            ? getPivotGroupKey(rowKey.pivotValues)
            : undefined;
        if (groupKey && !seenGroups.has(groupKey)) {
            if (seenGroups.size >= limit)
                return false;
            seenGroups.add(groupKey);
        }
        return true;
    });
};
const hasSamePivotFields = (series, expectedSeriesMap) => {
    const pivotValues = series.encode.yRef.pivotValues;
    if (!pivotValues || pivotValues.length === 0)
        return false;
    return Object.values(expectedSeriesMap).some((expectedSeries) => {
        const expectedPivots = expectedSeries.encode.yRef.pivotValues;
        return (expectedSeries.encode.yRef.field === series.encode.yRef.field &&
            expectedSeries.encode.xRef.field === series.encode.xRef.field &&
            !!expectedPivots &&
            expectedPivots.length === pivotValues.length &&
            pivotValues.every((pv) => expectedPivots.some((epv) => epv.field === pv.field)));
    });
};
const getRenderPropertiesByField = (existingSeries) => {
    const renderProperties = new Map();
    (existingSeries ?? []).forEach((series) => {
        const field = series.encode.yRef.field;
        if (renderProperties.has(field))
            return;
        renderProperties.set(field, {
            type: series.type,
            areaStyle: series.areaStyle,
            smooth: series.smooth,
            showSymbol: series.showSymbol,
            yAxisIndex: series.yAxisIndex ?? 0,
        });
    });
    return renderProperties;
};
export const getExpectedSeriesMap = ({ defaultSmooth, defaultShowSymbol, defaultCartesianType, defaultAreaStyle, isStacked, resultsData, pivotKeys, yFields, xField, defaultLabel, defaultStackLabel, itemsMap, columnLimit, existingSeries, defaultYAxisIndexByField, }) => {
    let expectedSeriesMap;
    // stackLabel is conditionally spread so series shape stays identical to
    // pre-fix when callers do not opt in. Adding `stackLabel: undefined`
    // unconditionally would surface a new key on every series and break
    // toStrictEqual snapshots used by other tests.
    const defaultProperties = {
        smooth: defaultSmooth,
        showSymbol: defaultShowSymbol,
        type: defaultCartesianType,
        areaStyle: defaultAreaStyle,
        yAxisIndex: 0,
        label: defaultLabel,
        ...(defaultStackLabel !== undefined && {
            stackLabel: defaultStackLabel,
        }),
    };
    const renderPropertiesByField = getRenderPropertiesByField(existingSeries);
    const getPropertiesForField = (field) => ({
        ...defaultProperties,
        yAxisIndex: defaultYAxisIndexByField?.[field] ?? 0,
        ...renderPropertiesByField.get(field),
    });
    if (pivotKeys && pivotKeys.length > 0) {
        const { rowKeyMap } = getPivotedDataFromPivotDetails(resultsData, itemsMap);
        let rowKeyValues = Object.values(rowKeyMap);
        if (columnLimit !== undefined && columnLimit > 0) {
            rowKeyValues = limitToFirstNPivotGroups(rowKeyValues, columnLimit);
        }
        expectedSeriesMap = rowKeyValues.reduce((acc, rowKey) => {
            let series;
            if (typeof rowKey === 'string') {
                series = {
                    ...getPropertiesForField(rowKey),
                    encode: {
                        xRef: { field: xField },
                        yRef: {
                            field: rowKey,
                        },
                    },
                };
            }
            else {
                const properties = getPropertiesForField(rowKey.field);
                series = {
                    ...properties,
                    encode: {
                        xRef: { field: xField },
                        yRef: rowKey,
                    },
                    stack: properties.areaStyle || isStacked
                        ? rowKey.field
                        : undefined,
                };
            }
            acc[getSeriesId(series)] = series;
            return acc;
        }, {});
    }
    else {
        expectedSeriesMap = (yFields || []).reduce((acc, yField) => {
            const properties = getPropertiesForField(yField);
            const series = {
                ...properties,
                encode: {
                    xRef: { field: xField },
                    yRef: {
                        field: yField,
                    },
                },
                stack: isStacked || !!properties.areaStyle
                    ? 'stack-all-series'
                    : undefined,
            };
            acc[getSeriesId(series)] = series;
            return acc;
        }, {});
    }
    return expectedSeriesMap;
};
// A pivoted chart's series order follows the SQL result's DENSE_RANK
// columnIndex, which the backend builds from the active sort. Re-sort
// merged series to that order when the sort references either a pivot
// dimension (PROD-2927) or a y-axis metric (PROD-2999) — both cases
// produce a deterministic pivot-value ranking that should drive the
// legend instead of the saved series order.
export const isPivotSeriesOrderDeterminedByQuery = (pivotKeys, yField, sorts) => !!pivotKeys?.length &&
    !!sorts?.some((sort) => pivotKeys.includes(sort.fieldId) ||
        !!yField?.includes(sort.fieldId));
export const mergeExistingAndExpectedSeries = ({ expectedSeriesMap, existingSeries, sortedByPivot, }) => {
    const existingValidSeries = [];
    const existingValidSeriesIds = new Set();
    for (const series of existingSeries) {
        const id = getSeriesId(series);
        const isSeriesExpected = Object.hasOwn(expectedSeriesMap, id);
        const isSeriesFilteredOut = !isSeriesExpected && hasSamePivotFields(series, expectedSeriesMap);
        if (isSeriesExpected || isSeriesFilteredOut) {
            existingValidSeries.push({
                ...series,
                isFilteredOut: isSeriesFilteredOut,
            });
            existingValidSeriesIds.add(id);
        }
    }
    if (existingValidSeries.length <= 0) {
        return Object.values(expectedSeriesMap);
    }
    // add missing series, inheriting properties from existing series in the same group
    const mergedSeries = Object.entries(expectedSeriesMap).reduce((acc, [expectedSeriesId, expectedSeries]) => {
        // Don't add the expected series if there is a valid one already
        if (existingValidSeriesIds.has(expectedSeriesId)) {
            return acc;
        }
        let seriesToAdd = expectedSeries;
        // For pivot series, inherit yAxisIndex from existing series with same field
        if (seriesToAdd.encode.yRef.pivotValues &&
            seriesToAdd.encode.yRef.pivotValues.length > 0) {
            const seriesInSameGroup = acc.find((series) => seriesToAdd.encode.yRef.field ===
                series.encode.yRef.field);
            if (seriesInSameGroup) {
                seriesToAdd = {
                    ...seriesToAdd,
                    yAxisIndex: seriesInSameGroup.yAxisIndex,
                };
            }
        }
        acc.push(seriesToAdd);
        return acc;
    }, [...existingValidSeries]);
    // Reorder series to match expectedSeriesMap order only when sorted by
    // a pivot dimension. This respects the query sort for grouped dimensions
    // while preserving manual series ordering when sorting by other fields.
    if (!sortedByPivot) {
        return mergedSeries;
    }
    const expectedSeriesIndex = new Map(Object.keys(expectedSeriesMap).map((id, index) => [id, index]));
    return [...mergedSeries].sort((a, b) => {
        const aIndex = expectedSeriesIndex.get(getSeriesId(a)) ?? -1;
        const bIndex = expectedSeriesIndex.get(getSeriesId(b)) ?? -1;
        if (aIndex === -1 && bIndex === -1)
            return 0;
        if (aIndex === -1)
            return 1;
        if (bIndex === -1)
            return -1;
        return aIndex - bIndex;
    });
};
export const getSeriesGroupedByField = (series) => {
    const seriesGroupMap = series.reduce((acc, obj, index) => {
        if (acc[obj.encode.yRef.field] &&
            !!obj.encode.yRef.pivotValues &&
            obj.encode.yRef.pivotValues.length > 0) {
            acc[obj.encode.yRef.field].value.push(obj);
            return acc;
        }
        acc[obj.encode.yRef.field] = { index, value: [obj] };
        return acc;
    }, {});
    return Object.values(seriesGroupMap).sort((a, b) => a.index - b.index);
};
export const moveSeriesGroup = (groups, from, to) => {
    if (from === to || from < 0 || from >= groups.length)
        return groups;
    const next = [...groups];
    const [moved] = next.splice(from, 1);
    next.splice(Math.min(Math.max(to, 0), next.length), 0, moved);
    return next;
};
export const sortDimensions = (dimensionIds, itemsMap, columnOrder) => {
    if (!itemsMap)
        return dimensionIds;
    if (dimensionIds.length <= 1)
        return dimensionIds;
    // Keyed by the map's own ids: a field's id is where it sits in the map.
    const dateDimensionIds = Object.entries(getDimensionsFromItemsMap(itemsMap))
        .filter(([dimensionId, dimension]) => isDimension(dimension) &&
        dimensionIds.includes(dimensionId) &&
        [DimensionType.DATE, DimensionType.TIMESTAMP].includes(dimension.type))
        .map(([dimensionId]) => dimensionId);
    switch (dateDimensionIds.length) {
        case 0:
            return dimensionIds; // No dates, we return the same order
        case 1: {
            // Only 1 date, we return this date first
            const [dateDimensionId] = dateDimensionIds;
            return [
                dateDimensionId,
                ...dimensionIds.filter((dimensionId) => dimensionId !== dateDimensionId),
            ];
        }
        default: {
            // 2 or more dates, we return first the date further left in the results table
            const sortedDateDimensionIds = [...dateDimensionIds].sort((a, b) => columnOrder.indexOf(a) - columnOrder.indexOf(b));
            return [
                ...sortedDateDimensionIds,
                ...dimensionIds.filter((dimensionId) => !sortedDateDimensionIds.includes(dimensionId)),
            ];
        }
    }
};
